import { Stack, Text } from "@certn/cascadia";
import {
  DateFieldFormikField,
  SelectFormikField,
  SelectItem,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { getLocalTimeZone, today } from "@internationalized/date";
import { useTranslation } from "react-i18next";

const UK_DBS_TITLE_OPTIONS = ["Mr", "Mrs", "Miss", "Ms", "Mx", "Dr"] as const;

interface IdentityOnDocumentSectionProps {
  isDisabled: boolean;
}

export function IdentityOnDocumentSection({
  isDisabled,
}: IdentityOnDocumentSectionProps) {
  const { t } = useTranslation();

  return (
    <div data-testid="identity-on-document-section">
      <Stack gap="space-2">
        <Text style="global/heading-large">
          {t(
            "Enter the applicant's details exactly as they appear on their ID"
          )}
        </Text>
      </Stack>
      <div className={styles.sectionContent}>
        <div className={styles.identityOnDocumentFields}>
          <SelectFormikField
            testID="manual-idv-identity-title"
            name="manual_idv_identity_on_document.title"
            label={t("Title")}
            isRequired
            isDisabled={isDisabled}
          >
            {UK_DBS_TITLE_OPTIONS.map((title) => (
              <SelectItem key={title} id={title} textValue={title}>
                {title}
              </SelectItem>
            ))}
          </SelectFormikField>
          <TextInputFormikField
            testID="manual-idv-identity-given-name"
            name="manual_idv_identity_on_document.given_name"
            label={t("Given name")}
            isRequired
            isDisabled={isDisabled}
          />
          <TextInputFormikField
            testID="manual-idv-identity-additional-name"
            name="manual_idv_identity_on_document.additional_name"
            label={t("Middle name")}
            isDisabled={isDisabled}
          />
          <TextInputFormikField
            testID="manual-idv-identity-family-name"
            name="manual_idv_identity_on_document.family_name"
            label={t("Family name")}
            isRequired
            isDisabled={isDisabled}
          />
          <DateFieldFormikField
            testID="manual-idv-identity-date-of-birth"
            name="manual_idv_identity_on_document.date_of_birth"
            label={t("Date of birth")}
            isRequired
            isDisabled={isDisabled}
            maxValue={today(getLocalTimeZone())}
            minValue={today(getLocalTimeZone()).subtract({ years: 120 })}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  sectionContent: css({
    paddingTop: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  identityOnDocumentFields: css({
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "var(--space-4)",
    maxWidth: "720px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  }),
};
